import type { ResourceMap } from './types';
import { boosterToInventoryMap, InventoryItem, TransactionReason } from './types';
import type { MetaProperties, ResourceActionTypeValueType } from '../tracking/trackingModules/avo/Avo';
import { ResourceActionType } from '../tracking/trackingModules/avo/Avo';
import type { LevelTrackingData } from '../state/AppStateController';
import type { TrackingModule } from '../tracking/types';

const reasonToActionMap: Record<TransactionReason, ResourceActionTypeValueType> = {
  [TransactionReason.LevelWinReward]: ResourceActionType.REWARD,
  [TransactionReason.DailyReward]: ResourceActionType.REWARD,
  [TransactionReason.LevelWinDoubleReward]: ResourceActionType.AD,
  [TransactionReason.BuyRevive]: ResourceActionType.COIN_PURCHASE,
  [TransactionReason.UseLife]: ResourceActionType.INVENTORY,
  [TransactionReason.BuyLives]: ResourceActionType.COIN_PURCHASE,
  [TransactionReason.LifeRequestReward]: ResourceActionType.REWARD,
  [TransactionReason.UseBooster]: ResourceActionType.INVENTORY,
  [TransactionReason.BuyBooster]: ResourceActionType.COIN_PURCHASE,
  [TransactionReason.ShopPurchase]: ResourceActionType.PURCHASE,
  [TransactionReason.LevelFailPopupPurchase]: ResourceActionType.PURCHASE,
  [TransactionReason.CoinBlocker]: ResourceActionType.COIN_PURCHASE,
  [TransactionReason.WinStreakReward]: ResourceActionType.REWARD,
  [TransactionReason.RaceEventReward]: ResourceActionType.REWARD,
  [TransactionReason.DioramaCompleteReward]: ResourceActionType.REWARD,
  [TransactionReason.ShopFreeGift]: ResourceActionType.REWARD,
  [TransactionReason.SeasonPassReward]: ResourceActionType.REWARD,
  [TransactionReason.LuckyWheelFreeSpinReward]: ResourceActionType.REWARD,
  [TransactionReason.LuckyWheelAdSpinReward]: ResourceActionType.AD,
  [TransactionReason.CollectionEventReward]: ResourceActionType.REWARD,
  [TransactionReason.DuelEntry]: ResourceActionType.COIN_PURCHASE,
  [TransactionReason.DuelReward]: ResourceActionType.REWARD,
  [TransactionReason.UseRewardedLuckyWheelFreeSpin]: ResourceActionType.INVENTORY,
};

const platformTrackedResourceItems = [InventoryItem.Coin, ...Object.values(boosterToInventoryMap)];

interface ResourceTrackerConfig {
  tracking: TrackingModule;
  getLevelTrackingData(): LevelTrackingData;
  getInventory(): Record<InventoryItem, number>;
  getSceneName(): string;
  platformTracking(eventName: string, amount: number): void;
}

export interface ResourceTrackingProps {
  gameEventId?: string;
  transactionId?: string;
  metaProps?: MetaProperties[];
}

export class ResourceTracker {
  constructor(private readonly config: ResourceTrackerConfig) {}

  public trackTransaction(change: ResourceMap, reason: TransactionReason, props: ResourceTrackingProps = {}): void {
    const { levelNumber, levelId, levelAttemptId } = this.config.getLevelTrackingData();

    this.config.tracking.trackResourceAction({
      levelNumber,
      levelId,
      levelAttemptId,
      actionTrigger: reason,
      actionType: reasonToActionMap[reason],
      resourceUpdate: change,
      inventory: this.config.getInventory(),
      gameEventId: props?.gameEventId,
      transactionId: props?.transactionId,
      metaProperties: [
        ...(props?.metaProps ?? []),
        {
          metaPropertyName: 'Scene',
          metaPropertyValue: this.config.getSceneName(),
        },
      ],
    });

    // Track individual resource usage on platform.
    Object.entries(change).forEach(([item, amount]) => {
      if (platformTrackedResourceItems.includes(item as InventoryItem)) {
        this.config.platformTracking(`Resource ${item}`, amount);
      }
    });

    this.fbBoosterUsedTracking(change);
  }

  private fbBoosterUsedTracking(change: ResourceMap): void {
    const boosters = Object.values(boosterToInventoryMap);
    const useAmount = Object.entries(change).reduce((acc, [key, value]) => {
      if (boosters.includes(key as InventoryItem) && value < 0) {
        return acc + Math.abs(value);
      }
      return acc;
    }, 0);

    this.config.platformTracking(`Booster_Used`, useAmount);
  }
}
